import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import { altapayQuote, setQuotePaymentMethodFree } from '../../../queries/altapay.graphql.js';
import { upsell } from '../../../queries/products.graphql.js';
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, BoxHeader, BoxBody } from '../../../components/Box/index.js';
import { ButtonLink } from '../../../components/ButtonLink/index.js';
import { Wrapper, Dialogue, Button, Foldable, ProductCard } from '@crossroads/ui-components';
import { useBrowser } from '@awardit/react-use-browser';
import { useTranslate } from '@awardit/react-use-translate';
import { updateQuoteItemQty, removeQuoteItem, syncQuote } from '@crossroads/shop-state/quote';
import { useSendMessage, useData } from 'crustate/react';
import { QtyPicker } from '../../../components/QtyPicker/index.js';
import { DiscountCode } from '../../../components/DiscountCode/index.js';
import { OrderSummary } from '../../../components/OrderSummary/index.js';
import { SuggestedProducts } from '../../../components/SuggestedProducts/index.js';
import cn from 'classnames';
import { CrossSvg, ChevronBigSvg } from '../../../icons/index.js';
import styles from 'shop-views/CheckoutCart.scss';

var _excluded = ["analytics", "QuoteData", "useClient", "quoteQuery", "formatPrice", "formatPoints", "RedeemCardField", "setQuoteDiscountCode", "removeQuoteDiscountCode", "SummaryField", "BtmColPrimaryField", "BtmColSecondaryField", "hasBrandView", "ProductCardComponent", "suggestedProductsQuery"];

function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

function _empty() {}

function _invokeIgnored(body) {
  var result = body();

  if (result && result.then) {
    return result.then(_empty);
  }
}

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

var QUOTE_ITEM_UPDATING_STATES = new Set(["UPDATING_ITEM", "REMOVING_ITEM"]);
var CheckoutCart = _ref => {
  var _quote$data3;

  function _temp2(result) {
    return _invokeIgnored(function () {
      if (result.setQuotePaymentMethod.free.result === "success") {
        return _await(client(quoteQuery), _temp);
      }
    });
  }

  function _temp(newQuoteData) {
    if (newQuoteData.quote) {
      sendMessage(syncQuote(newQuoteData.quote));
    }
  }

  var _ref$analytics = _ref.analytics;
  _ref$analytics = _ref$analytics === void 0 ? {} : _ref$analytics;

  var context = _ref$analytics.context,
      _ref$analytics$hasAlt = _ref$analytics.hasAlternativeContext,
      hasAlternativeContext = _ref$analytics$hasAlt === void 0 ? false : _ref$analytics$hasAlt,
      _ref$analytics$hasExV = _ref$analytics.hasExVat,
      hasExVat = _ref$analytics$hasExV === void 0 ? false : _ref$analytics$hasExV,
      QuoteData = _ref.QuoteData,
      useClient = _ref.useClient,
      _ref$quoteQuery = _ref.quoteQuery,
      quoteQuery = _ref$quoteQuery === void 0 ? altapayQuote : _ref$quoteQuery,
      formatPrice = _ref.formatPrice,
      formatPoints = _ref.formatPoints,
      RedeemCardField = _ref.RedeemCardField,
      setQuoteDiscountCode = _ref.setQuoteDiscountCode,
      removeQuoteDiscountCode = _ref.removeQuoteDiscountCode,
      SummaryField = _ref.SummaryField,
      BtmColPrimaryField = _ref.BtmColPrimaryField,
      BtmColSecondaryField = _ref.BtmColSecondaryField,
      _ref$hasBrandView = _ref.hasBrandView,
      hasBrandView = _ref$hasBrandView === void 0 ? true : _ref$hasBrandView,
      _ref$ProductCardCompo = _ref.ProductCardComponent,
      ProductCardComponent = _ref$ProductCardCompo === void 0 ? ProductCard : _ref$ProductCardCompo,
      _ref$suggestedProduct = _ref.suggestedProductsQuery,
      suggestedProductsQuery = _ref$suggestedProduct === void 0 ? upsell : _ref$suggestedProduct,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useState = useState(false),
      confirmDialogueOpen = _useState[0],
      setConfirmDialogueOpen = _useState[1];

  var _useState2 = useState(null),
      activeItem = _useState2[0],
      setActiveItem = _useState2[1];

  var sendMessage = useSendMessage();
  var quote = useData(QuoteData);
  var t = useTranslate();
  var client = useClient();

  var _useState3 = useState(false),
      isUpdatingQuote = _useState3[0],
      setIsUpdatingQuote = _useState3[1];

  useEffect(() => {
    if (QUOTE_ITEM_UPDATING_STATES.has(quote.state)) {
      setIsUpdatingQuote(true);
    } else {
      setIsUpdatingQuote(false);
    }
  }, [quote.state]);
  useEffect(() => {
    if (quote.data) {
      var _quote$data = quote.data,
          grandTotal = _quote$data.grandTotal,
          _items = _quote$data.items;
      var vat = hasExVat ? grandTotal.exVat : grandTotal.incVat;
      context.viewedCart(vat, _items.map(item => {
        var _item$sku;

        return {
          sku: (_item$sku = item.sku) != null ? _item$sku : item.product.sku,
          name: item.product.name,
          categories: item.product.categories,
          qty: item.qty,
          price: {
            exVat: item.rowTotal.exVat,
            incVat: item.rowTotal.incVat,
            vat: item.rowTotal.incVat - item.rowTotal.exVat
          },
          attributes: {
            manufacturer: item.product.attributes.manufacturer
          }
        };
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    var setPaymentMethodFree = _async(function () {
      return _await(client(setQuotePaymentMethodFree), _temp2);
    });

    if (quote.state === "LOADED") {
      var _quote$data2 = quote.data,
          grandTotal = _quote$data2.grandTotal,
          payment = _quote$data2.payment; // If payment is null altapay component sets setQuotePaymentMethodAltapay

      if (grandTotal.incVat === 0 && (payment == null ? void 0 : payment.code) !== "free") {
        setPaymentMethodFree();
      }
    }
  }, [client, quoteQuery, quote, sendMessage]); // Shops that uses FB Pixel and TikTok tracking have an analyticsContext
  // locally in their repository that needs different
  // data than @crossroads/analytics

  var sendGAEvent = (product, diff) => {
    var modifiedDiff = hasAlternativeContext ? diff : diff > 0 ? "add_to_cart" : "remove_from_cart";
    var vat = hasExVat ? product.price.exVat : product.price.incVat;
    context.registerModifyCart({
      sku: product.sku,
      name: product.name,
      price: product.price,
      qty: Math.abs(diff),
      attributes: {
        manufacturer: product.attributes.manufacturer
      },
      categories: product.categories
    }, modifiedDiff, vat);
  };

  if (!quote.data) {
    return null;
  }

  var items = ((_quote$data3 = quote.data) == null ? void 0 : _quote$data3.items) || [];

  var displayRemoveDialogue = item => {
    setConfirmDialogueOpen(true);
    setActiveItem(item);
  };

  var remove = item => {
    sendGAEvent({
      sku: item.product.sku,
      name: item.product.name,
      price: item.product.price,
      qty: item.qty,
      attributes: {
        manufacturer: item.product.attributes.manufacturer
      },
      categories: item.product.categories
    }, -item.qty);
    sendMessage(removeQuoteItem(item.itemBuyRequest));
  };

  if (quote.state === "LOADED" && items.length === 0) {
    return /*#__PURE__*/React.createElement(Wrapper, {
      className: styles.container
    }, /*#__PURE__*/React.createElement(Helmet, {
      title: t("CART.HEADING")
    }), /*#__PURE__*/React.createElement("header", {
      className: styles.header
    }, /*#__PURE__*/React.createElement("h1", null, t("CART.EMPTY")), /*#__PURE__*/React.createElement(ButtonLink, {
      className: styles.back,
      to: "/"
    }, /*#__PURE__*/React.createElement("span", null, t("CART.CONTINUE_SHOPPING")))));
  }

  return /*#__PURE__*/React.createElement(Container, {
    right: /*#__PURE__*/React.createElement(CheckoutRightField, {
      quote: quote,
      isUpdatingQuote: isUpdatingQuote,
      setIsUpdatingQuote: setIsUpdatingQuote,
      RedeemCardField: RedeemCardField,
      formatPrice: formatPrice,
      formatPoints: formatPoints,
      setQuoteDiscountCode: setQuoteDiscountCode,
      removeQuoteDiscountCode: removeQuoteDiscountCode,
      SummaryField: SummaryField,
      BtmColSecondaryField: BtmColSecondaryField
    })
  }, confirmDialogueOpen && /*#__PURE__*/React.createElement(Dialogue, {
    className: styles.confirmDialogue,
    open: confirmDialogueOpen,
    setOpen: () => setConfirmDialogueOpen(false),
    title: t("CART.REMOVE_PRODUCT"),
    closeIcon: /*#__PURE__*/React.createElement(Button, {
      "aria-label": t("CART.CLOSE_DIALOG"),
      className: styles.closeIconWrapper
    }, /*#__PURE__*/React.createElement("img", {
      src: CrossSvg,
      className: styles.closeIcon,
      alt: t("ICONS.CROSS")
    })),
    primaryAction: t("CART.REMOVE_APPROVE"),
    secondaryAction: t("CART.DIALOG_CANCEL"),
    onPrimaryAction: () => {
      if (activeItem) {
        setConfirmDialogueOpen(false);
        remove(activeItem);
      }
    },
    onSecondaryAction: () => setConfirmDialogueOpen(false)
  }, t("CART.CONFIRM_TEXT", {
    itemName: `${(activeItem == null ? void 0 : activeItem.product.name) || ""}, ${(activeItem == null ? void 0 : activeItem.product.attributes.manufacturer) || ""}`
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.cart
  }, items.map(item => /*#__PURE__*/React.createElement(CheckoutCartItem, {
    key: item.itemBuyRequest,
    item: item,
    quote: quote,
    formatPrice: formatPrice,
    displayRemoveDialogue: displayRemoveDialogue,
    sendGAEvent: sendGAEvent,
    hasBrandView: hasBrandView
  }))), /*#__PURE__*/React.createElement(SuggestedProducts, _extends({}, rest, {
    ProductCardComponent: ProductCardComponent,
    useClient: useClient,
    suggestedProductsQuery: suggestedProductsQuery
  })), BtmColPrimaryField);
};

var CheckoutCartItem = _ref2 => {
  var quote = _ref2.quote,
      item = _ref2.item,
      formatPrice = _ref2.formatPrice,
      displayRemoveDialogue = _ref2.displayRemoveDialogue,
      sendGAEvent = _ref2.sendGAEvent,
      hasBrandView = _ref2.hasBrandView;
  var sendMessage = useSendMessage();
  var product = item.configOption ? _extends({}, item.product, item.configOption.product, {
    categories: item.product.categories
  }) : item.product;
  var processingItem = typeof quote.processingItem !== "undefined" ? quote.processingItem : null;
  var _product$attributes = product.attributes,
      image = _product$attributes.image,
      manufacturer = _product$attributes.manufacturer;
  var minQty = 0;
  return /*#__PURE__*/React.createElement("div", {
    key: item.itemBuyRequest,
    className: cn(styles.item, {
      [styles.processing]: processingItem === item.itemBuyRequest
    }, {
      [styles.disabled]: quote.state === "UPDATING_ITEM"
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.left
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: "flex",
      position: "static"
    }
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.image,
    alt: product.name,
    src: image == null ? void 0 : image.x1
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.info
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Link, {
    className: styles.name,
    to: {
      pathname: product.url,
      state: {
        hint: {
          type: "product",
          product,
          image: image == null ? void 0 : image.x1
        }
      }
    }
  }, product.name), hasBrandView ? /*#__PURE__*/React.createElement(Link, {
    className: styles.brand,
    to: {
      pathname: `/brand/${encodeURIComponent(manufacturer)}`,
      state: {
        hint: {
          type: "category",
          category: {
            name: manufacturer
          }
        }
      }
    }
  }, manufacturer) : /*#__PURE__*/React.createElement("span", {
    className: styles.brand
  }, manufacturer), item.bundleOptions && /*#__PURE__*/React.createElement("ul", {
    className: styles.bundleOptions
  }, item.bundleOptions.map(o => o.products.map(p => /*#__PURE__*/React.createElement("li", {
    key: o.title
  }, p.product.name)))))))), /*#__PURE__*/React.createElement("div", {
    className: styles.right
  }, /*#__PURE__*/React.createElement(QtyPicker, {
    className: styles.qtyPicker,
    value: item.qty,
    min: minQty,
    setValue: v => {
      if (v > minQty) {
        sendMessage(updateQuoteItemQty(item.itemBuyRequest, v));
        sendGAEvent(_extends({}, product, {
          qty: Math.abs(v - item.qty)
        }), v - item.qty);
      } else {
        displayRemoveDialogue(item);
      }
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.priceWrapper
  }, /*#__PURE__*/React.createElement("p", null, formatPrice(item.qty * item.product.price.incVat || 0)))));
};

var CheckoutRightField = _ref3 => {
  var quote = _ref3.quote,
      isUpdatingQuote = _ref3.isUpdatingQuote,
      setIsUpdatingQuote = _ref3.setIsUpdatingQuote,
      RedeemCardField = _ref3.RedeemCardField,
      formatPrice = _ref3.formatPrice,
      formatPoints = _ref3.formatPoints,
      setQuoteDiscountCode = _ref3.setQuoteDiscountCode,
      removeQuoteDiscountCode = _ref3.removeQuoteDiscountCode,
      SummaryField = _ref3.SummaryField,
      BtmColSecondaryField = _ref3.BtmColSecondaryField,
      _ref3$vatToUse = _ref3.vatToUse,
      vatToUse = _ref3$vatToUse === void 0 ? "incVat" : _ref3$vatToUse;

  var _ref4 = quote.data || {},
      coupon = _ref4.coupon;

  var t = useTranslate();
  var isBrowser = useBrowser();
  var summaryRef = useRef(null);

  var _useState4 = useState(true),
      summaryOpen = _useState4[0],
      setSummaryOpen = _useState4[1];

  var _useState5 = useState(0),
      summaryHeight = _useState5[0],
      setSummaryHeight = _useState5[1];

  var activeRedeem = quote.data && quote.data.retain24validation;
  var smallSummaryOnClose = true;
  var useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

  var toggleSummaryOpen = () => {
    if (setSummaryOpen) {
      setSummaryOpen(!summaryOpen);
    }
  };

  useIsomorphicLayoutEffect(() => {
    if (summaryRef.current && isBrowser) {
      setSummaryHeight(`${summaryRef.current.clientHeight}px`);
    }
  }, [isBrowser, summaryRef]);
  useEffect(() => {
    var current = summaryRef.current;

    var handleSummaryToggle = () => {
      if (current) {
        setSummaryHeight(`${current.clientHeight}px`);
      }
    };

    if (current) {
      current.addEventListener("transitionend", handleSummaryToggle);
      return () => current.removeEventListener("transitionend", handleSummaryToggle);
    }
  }, []);
  useEffect(() => {
    if (QUOTE_ITEM_UPDATING_STATES.has(quote.state)) {
      setIsUpdatingQuote(true);
    } else {
      setIsUpdatingQuote(false);
    }
  }, [quote.state]);

  if (activeRedeem && !quote.data) {
    return null;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: summaryRef,
    className: styles.summary
  }, SummaryField !== null && SummaryField !== undefined ? SummaryField : /*#__PURE__*/React.createElement(Box, {
    className: cn(styles.box, {
      [styles.open]: summaryOpen
    })
  }, /*#__PURE__*/React.createElement(BoxHeader, {
    className: cn(styles.boxHeader, styles.boxHeaderCustom),
    onClick: toggleSummaryOpen
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-expanded": summaryOpen != null ? summaryOpen : "true",
    "aria-controls": "orderSummary",
    className: styles.summaryControls
  }, /*#__PURE__*/React.createElement("h2", {
    className: styles.summaryTitle
  }, t("CART.SUMMARY")), /*#__PURE__*/React.createElement("img", {
    src: ChevronBigSvg,
    alt: summaryOpen ? t("CART.CLOSE_SUMMARY") : t("CART.OPEN_SUMMARY"),
    className: styles.chevronCloseIcon
  })), /*#__PURE__*/React.createElement(Foldable, {
    id: "orderSummary",
    open: !summaryOpen,
    className: styles.foldableTotal
  }, /*#__PURE__*/React.createElement(OrderSummary, {
    isGrandTotalOnly: smallSummaryOnClose,
    quoteData: quote,
    formatPrice: formatPrice,
    formatPoints: formatPoints,
    vatToUse: vatToUse
  }))), /*#__PURE__*/React.createElement(BoxBody, {
    className: styles.boxBody
  }, /*#__PURE__*/React.createElement(Foldable, {
    open: summaryOpen
  }, /*#__PURE__*/React.createElement(OrderSummary, {
    quoteData: quote,
    formatPrice: formatPrice,
    formatPoints: formatPoints,
    vatToUse: vatToUse
  }), "\xA0"), /*#__PURE__*/React.createElement("div", {
    className: styles.submitWrapper
  }, /*#__PURE__*/React.createElement(ButtonLink, {
    className: styles.submitButton,
    variant: "primary",
    disabled: isUpdatingQuote,
    to: t("URL.CHECKOUT_OVERVIEW")
  }, t("CART.TO_CHECKOUT")))))), RedeemCardField !== null && RedeemCardField !== undefined ? /*#__PURE__*/React.createElement("div", {
    className: styles.redeemOuter
  }, RedeemCardField) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.discountOuter
  }, /*#__PURE__*/React.createElement(DiscountCode, {
    coupon: coupon,
    disabled: isUpdatingQuote,
    setQuoteDiscountCode: setQuoteDiscountCode,
    removeQuoteDiscountCode: removeQuoteDiscountCode
  })), BtmColSecondaryField !== null && BtmColSecondaryField !== undefined ? /*#__PURE__*/React.createElement("div", {
    className: styles.btmColSecOuter
  }, BtmColSecondaryField) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      height: summaryHeight
    },
    className: styles.btmPaddingBlock
  }));
};

var Container = _ref5 => {
  var children = _ref5.children,
      right = _ref5.right;
  var t = useTranslate();
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: styles.container
  }, /*#__PURE__*/React.createElement(Helmet, {
    title: t("CART.HEADING")
  }), /*#__PURE__*/React.createElement("header", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("h1", null, t("CART.HEADING")), /*#__PURE__*/React.createElement(Link, {
    className: styles.back,
    to: "/"
  }, /*#__PURE__*/React.createElement("span", null, t("CART.CONTINUE_SHOPPING")))), /*#__PURE__*/React.createElement("div", {
    className: styles.containerMain
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.containerLeft
  }, children), /*#__PURE__*/React.createElement("div", {
    className: styles.containerRight
  }, right)));
};

export { CheckoutCart };
//# sourceMappingURL=index.js.map
